import React, { useState } from "react";
import { Mail, Mic2, BookOpen, User, ArrowUpRight } from "lucide-react";
import PageShell from "./_PageShell";
import { EMAILS, mailtoLink } from "../config/contacts";

const ROUTES = [
  {
    key: "hello",
    eyebrow: "General Inquiry",
    title: "Get in Touch",
    body:
      "Questions about the website, the work, or where to begin? Send a short note and the team will route it to the right place.",
    address: EMAILS.hello,
    Icon: Mail,
    testid: "contact-route-hello",
    cta: "Email hello@theiyare.com",
  },
  {
    key: "booking",
    eyebrow: "Speaking · Workshops · Booking",
    title: "Book Iyare",
    body:
      "Khutbahs, keynotes, school visits, workshops, conferences and consulting engagements. Include date, location, audience and topic.",
    address: EMAILS.booking,
    Icon: BookOpen,
    testid: "contact-route-booking",
    cta: "Email booking@theiyare.com",
  },
  {
    key: "podcast",
    eyebrow: "Podcast · Media · Collaborations",
    title: "Become a Guest",
    body:
      "Guest pitches, press, interviews, and collaborative projects with The Murabbi Channel and Iyare's published work.",
    address: EMAILS.podcast,
    Icon: Mic2,
    testid: "contact-route-podcast",
    cta: "Email podcast@theiyare.com",
  },
  {
    key: "me",
    eyebrow: "Personal Correspondence",
    title: "Contact Iyare",
    body:
      "For personal letters, mentees, students, family of past students, and longstanding colleagues. Read personally.",
    address: EMAILS.me,
    Icon: User,
    testid: "contact-route-me",
    cta: "Email me@theiyare.com",
  },
];

const RouteCard = ({ route }) => {
  const { eyebrow, title, body, address, Icon, testid, cta, key } = route;
  return (
    <a
      href={mailtoLink(key)}
      data-testid={testid}
      className="group relative flex flex-col border border-white/10 hover:border-azure/40 bg-ink-soft/40 transition-colors duration-500"
    >
      <span
        aria-hidden="true"
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 80% 100% at 0% 0%, rgba(74,143,229,0.10), transparent 70%)",
        }}
      />
      <div className="relative p-6 lg:p-7 flex flex-col h-full">
        <div className="flex items-start justify-between gap-4">
          <div className="text-[10.5px] tracking-[0.34em] uppercase text-azure-soft">
            {eyebrow}
          </div>
          <span className="w-9 h-9 rounded-full border border-azure-soft/40 inline-flex items-center justify-center text-azure-soft shrink-0">
            <Icon className="w-3.5 h-3.5" strokeWidth={1.6} />
          </span>
        </div>

        <div className="mt-6 h-px w-10 bg-azure-soft/50" />

        <h3 className="mt-6 font-serif text-white text-[22px] lg:text-[24px] leading-[1.18] tracking-[-0.015em] group-hover:text-azure-soft transition-colors duration-500">
          {title}
        </h3>

        <p className="mt-4 text-white/65 text-[14.5px] leading-[1.85] max-w-[55ch]">
          {body}
        </p>

        <div className="mt-6 text-white/50 text-[13px] font-mono tracking-tight">
          {address}
        </div>

        <div className="mt-auto pt-7 inline-flex items-center gap-3 text-[10.5px] tracking-[0.34em] uppercase text-white/55 group-hover:text-azure-soft transition-colors duration-500">
          {cta}
          <ArrowUpRight
            className="w-3.5 h-3.5 transition-transform duration-500 group-hover:translate-x-1.5 group-hover:-translate-y-1.5"
            strokeWidth={1.6}
          />
        </div>
      </div>
    </a>
  );
};

const Contact = () => {
  const [submitted, setSubmitted] = useState(false);

  const onSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <PageShell
      testId="contact-page"
      eyebrow="Contact"
      title="Let's begin a conversation."
      intro="Choose the inbox that fits best — every message is read and routed by hand. Responses arrive within three business days, inshaAllah."
    >
      {/* Four-inbox routing grid */}
      <div className="grid md:grid-cols-2 gap-5 lg:gap-6 mb-14 lg:mb-16">
        {ROUTES.map((r) => (
          <RouteCard key={r.key} route={r} />
        ))}
      </div>

      {/* Fallback form — routes to the general inbox */}
      <div className="grid md:grid-cols-5 gap-12 pt-6 border-t border-white/5">
        <div className="md:col-span-2 space-y-6">
          <div className="text-[10.5px] tracking-[0.34em] uppercase text-gold">
            Prefer a Form?
          </div>
          <h2 className="font-serif text-white text-[24px] lg:text-[28px] leading-[1.18] tracking-[-0.012em]">
            Write a note. We&apos;ll route it.
          </h2>
          <p className="text-white/65 text-[14.5px] leading-[1.9] max-w-sm">
            Not sure which inbox is right? Drop a message here and the team will
            forward it to the appropriate address.
          </p>
          <div className="text-[10.5px] tracking-[0.34em] uppercase text-gold pt-4">
            Response Time
          </div>
          <div className="text-white/70 text-sm">
            Within 3 business days, inshaAllah.
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="contact-form"
          className="md:col-span-3 space-y-5 border border-white/10 bg-ink-soft/30 p-8"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="text-[10px] tracking-[0.32em] uppercase text-gold">Name</label>
              <input data-testid="contact-name-input" required className="mt-2 w-full bg-transparent border-b border-white/20 focus:border-gold outline-none py-2 text-white" />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.32em] uppercase text-gold">Email</label>
              <input type="email" data-testid="contact-email-input" required className="mt-2 w-full bg-transparent border-b border-white/20 focus:border-gold outline-none py-2 text-white" />
            </div>
          </div>
          <div>
            <label className="text-[10px] tracking-[0.32em] uppercase text-gold">Subject</label>
            <input data-testid="contact-subject-input" className="mt-2 w-full bg-transparent border-b border-white/20 focus:border-gold outline-none py-2 text-white" />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.32em] uppercase text-gold">Message</label>
            <textarea data-testid="contact-message-input" required rows={5} className="mt-2 w-full bg-transparent border-b border-white/20 focus:border-gold outline-none py-2 text-white resize-none" />
          </div>
          <button
            type="submit"
            data-testid="contact-submit-btn"
            className="inline-flex items-center justify-center bg-azure hover:bg-[#5C9BEC] text-white px-7 py-3 text-[11px] tracking-[0.32em] uppercase font-medium transition-all"
          >
            Send Message
          </button>
          {submitted && (
            <div data-testid="contact-success" className="text-gold text-sm">
              JazakAllahu khayran — your message has been received.
            </div>
          )}
        </form>
      </div>
    </PageShell>
  );
};

export default Contact;
